/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { WorkPlaceDetailsProps } from "./interfaces";
import { responsiveStyles } from "../../commons/styles";
import { workplaceDetailsStyle } from "./styles";
import {
  booleanRadioValues,
  ButtonClickType,
  inputFieldMandatoryLabel,
  inputFieldOptionalLabel,
} from "../../constants/commons";
import * as _ from "lodash";
import { URL } from "../../API/urlEndpoints";
import { RadioButtonSelect } from "../../atoms/RadioButtonSelect";
import { ChangeEvent } from "react";
import useSTR from "react-str";
import { getData, postData } from "../../utils/fetchers";
import {
  WorkspaceInfo,
  BooleanTypes,
  RegisterationFields,
  FieldRenderState,
} from "../../commons/interfaces";
import { toast } from "../../utils/toast";
import { RegistrationFooterButton } from "../../components/RegistrationFooterButton";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import {
  displayConfirmationWhenWindowCloses,
  getErrorForField,
  getFieldConfigStatus,
  isFieldOptional,
  showDisplayHeadingAndContentFromLoadingStatus,
  testNetworkSpeed,
} from "../../utils/helpers";
import errorLogo from "../../assets/error-icon.svg";
import { useIsMobile, useIsTab } from "../../hooks/useIsMobile";
import { ShowMessage } from "../../components/ShowMessage";
import { useUserContext } from "../../hooks/cookieHooks";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import successLogo from "../../assets/success-icon.svg";
import wifiLogo from "../../assets/wifi.svg";

export const WorkPlaceDetails: React.FunctionComponent<WorkPlaceDetailsProps> = (
  props
) => {
  const isMobileView = useIsMobile();
  const isTabView = useIsTab();
  const {
    pageNavigationDetails,
    onNextClick,
    onBackButtonClick,
    onSaveAndExitClick,
    SuccessFullSaveObj,
    isLastStep,
  } = props;
  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();
  const { nextScreen } = pageNavigationDetails;
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();
  // State to maintain the loading state of speedtest API call
  const [loadingSpeedTest, setLoadingSpeedTest] = React.useState<boolean>(
    false
  );
  // State to maintain the speedtest Value
  const [speedtestValue, setSpeedtestValue] = React.useState<number>();

  // [GET] On page load, for fetching the details of Basic Information
  const [
    ,
    {
      result: getWorkplaceDetails,
      revalidator: refetchWorkplaceDetails,
      loading: getWorkplaceDetailsLoading,
      error: getWorkplaceDetailsError,
    },
  ] = useSTR(getData);

  // [POST] For Saving data on page.
  const [
    ,
    {
      result: postWorkplaceDetails,
      revalidator: refetchPostWorkplaceDetails,
      loading: postWorkplaceDetailsLoading,
      error: postWorkplaceDetailsError,
    },
  ] = useSTR(postData);

  // On page load, calling the GET API
  React.useEffect(() => {
    refetchWorkplaceDetails(`${URL.getWorkplaceDetails(_.get(user, "empId"))}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State to check whether workplace is Selected or not
  const [workplaceExist, setWorkplaceExist] = React.useState<string>();
  // State checking whether user can be navigated to the next page or not
  const [
    isNextPageNavAllowed,
    setIsNextPageNavAllowed,
  ] = React.useState<boolean>(false);

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const constructDataForPOSTAPI = React.useCallback(() => {
    let workPlaceDetailsObj: WorkspaceInfo = {
      isPrivateWorkSpaceAvailable: workplaceExist as BooleanTypes,
      maxInternetSpeedAvailable: speedtestValue, //TODO: Integrate it with the NETWORK API
    };
    return workPlaceDetailsObj;
  }, [workplaceExist, speedtestValue]);

  React.useEffect(() => {
    if (getWorkplaceDetails) {
      const {
        isPrivateWorkSpaceAvailable,
        maxInternetSpeedAvailable,
      } = getWorkplaceDetails;
      // Internet Speed Test
      maxInternetSpeedAvailable && setSpeedtestValue(maxInternetSpeedAvailable);
      // Private Workplace
      isPrivateWorkSpaceAvailable &&
        setWorkplaceExist(isPrivateWorkSpaceAvailable.toUpperCase());
    }
  }, [getWorkplaceDetails]);

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // Showing the success message in a toast component / Fetching data From Server
  React.useEffect(() => {
    if (postWorkplaceDetails) {
      postWorkplaceDetails?.message &&
        toast.success(postWorkplaceDetails.message);
      if (isNextPageNavAllowed) {
        onNextClick(nextScreen);
      } else {
        SuccessFullSaveObj(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postWorkplaceDetails, isNextPageNavAllowed]);

  // Function which is called when secondary language is selected from dropdwon
  const onWorkPlaceChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWorkplaceExist(event.target.value);
  };

  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    const workPlaceDetailsObj = constructDataForPOSTAPI();

    switch (buttonClickType) {
      case ButtonClickType.NEXT:
        let paramsObj = {
          ...workPlaceDetailsObj,
          customHeader: { change_state: true },
        };
        refetchPostWorkplaceDetails(
          `${URL.postWorkplaceDetails(_.get(user, "empId"))}`,
          paramsObj
        );
        setIsNextPageNavAllowed(true);
        break;

      case ButtonClickType.SAVE_AND_EXIT:
        refetchPostWorkplaceDetails(
          `${URL.postWorkplaceDetails(_.get(user, "empId"))}`,
          workPlaceDetailsObj
        );
        setIsNextPageNavAllowed(false);
        break;

      default:
        break;
    }
  };

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return (
      getWorkplaceDetailsLoading ||
      postWorkplaceDetailsLoading ||
      loadingSpeedTest
    );
  }, [
    getWorkplaceDetailsLoading,
    postWorkplaceDetailsLoading,
    loadingSpeedTest,
  ]);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return getWorkplaceDetailsError || postWorkplaceDetailsError;
  }, [postWorkplaceDetailsError, getWorkplaceDetailsError]);

  // For showing/hiding the Workplace Exists radio Button
  const showWorkPLaceExistsComponent = React.useMemo(() => {
    return (
      getWorkplaceDetails &&
      getWorkplaceDetails.fieldsconfig &&
      _.keys(getWorkplaceDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getWorkplaceDetails.fieldsconfig,
        RegisterationFields.PRIVATE_WORKPLACE
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getWorkplaceDetails]);

  // For showing errors [Workplace Exists]
  const workplaceDetailsError = React.useMemo(() => {
    return (
      getWorkplaceDetails &&
      getWorkplaceDetails.errors &&
      getErrorForField(
        RegisterationFields.PRIVATE_WORKPLACE,
        getWorkplaceDetails.errors
      )
    );
  }, [getWorkplaceDetails]);

  // For showing the label OPTIONAL for the Workplace Exists radio Button
  const showWorkPLaceExistsLabel = React.useMemo(() => {
    return (
      getWorkplaceDetails &&
      getWorkplaceDetails.fieldsconfig &&
      _.keys(getWorkplaceDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getWorkplaceDetails.fieldsconfig,
          RegisterationFields.PRIVATE_WORKPLACE
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getWorkplaceDetails]);

  const checkNetworkSpeed = () => {
    setLoadingSpeedTest(true);
  };

  React.useEffect(() => {
    loadingSpeedTest && APIcallToTestNetworkSpeed();
  }, [loadingSpeedTest]);

  const APIcallToTestNetworkSpeed = () => {
    testNetworkSpeed((result) => {
      setSpeedtestValue(result);
      setLoadingSpeedTest(false);
    });
  };

  const showInternetSpeed = React.useCallback(() => {
    return speedtestValue ? (
      <div className="outer-speed-container d-flex align-items-center m-3 px-5">
        <img src={successLogo} alt="" width={isMobileView ? "8%" : "4%"} />
        <div className="success-description ml-3">
          Stable Internet connection
        </div>
        <div className="success-description ml-auto">
          {speedtestValue + " Mbps"}
        </div>
      </div>
    ) : (
      <React.Fragment></React.Fragment>
    );
  }, [speedtestValue, isMobileView]);

  // For showing/hiding the Internet Connection
  const showInternetSpeedComponent = React.useMemo(() => {
    return (
      getWorkplaceDetails &&
      getWorkplaceDetails.fieldsconfig &&
      _.keys(getWorkplaceDetails.fieldsconfig).length > 0 &&
      getFieldConfigStatus(
        getWorkplaceDetails.fieldsconfig,
        RegisterationFields.STABLE_INTERNET_CONNECTIVITY
      )[0] !== FieldRenderState.DISABLED
    );
  }, [getWorkplaceDetails]);

  // For showing the label OPTIONAL for the field INternet Connection
  const showInternetSpeedLabel = React.useMemo(() => {
    return (
      getWorkplaceDetails &&
      getWorkplaceDetails.fieldsconfig &&
      _.keys(getWorkplaceDetails.fieldsconfig).length > 0 &&
      isFieldOptional(
        getFieldConfigStatus(
          getWorkplaceDetails.fieldsconfig,
          RegisterationFields.STABLE_INTERNET_CONNECTIVITY
        )[0]
      ) &&
      inputFieldOptionalLabel
    );
  }, [getWorkplaceDetails]);

  const isSubmitAllowed = React.useMemo(() => {
    return !(
      workplaceExist && (showInternetSpeedComponent ? speedtestValue : true)
    );
  }, [workplaceExist, showInternetSpeedComponent, speedtestValue]);

  return (
    <span css={[responsiveStyles, workplaceDetailsStyle]}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getWorkplaceDetailsLoading,
            postLoginScreen: postWorkplaceDetailsLoading,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getWorkplaceDetailsLoading,
            postLoginScreen: postWorkplaceDetailsLoading,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>Work Place Details</h2>
        {showWorkPLaceExistsComponent && (
          <div className="my-3">
            <div className="d-flex">
              <h6>Do you have a private workspace?</h6>
              <div className="small-text ml-2">
                {showWorkPLaceExistsLabel ? (
                  showWorkPLaceExistsLabel
                ) : (
                  <div className="responsive-error-asterix-style">
                    {inputFieldMandatoryLabel}
                  </div>
                )}
              </div>
            </div>

            <RadioButtonSelect
              value={booleanRadioValues}
              defaultValue={workplaceExist}
              onValueChange={onWorkPlaceChange}
            />
            {workplaceDetailsError && (
              <div className="error-text">{workplaceDetailsError}</div>
            )}
          </div>
        )}
        {showInternetSpeedComponent && (
          <div>
            <div className="my-3">
              <div className="d-flex">
                <h6>Your internet connectivity</h6>
                <span className="small-text ml-2">
                  {showInternetSpeedLabel ? (
                    showInternetSpeedLabel
                  ) : (
                    <div className="responsive-error-asterix-style">
                      {inputFieldMandatoryLabel}
                    </div>
                  )}
                </span>
              </div>

              <div className="col px-0">
                <button
                  className="wifi-button user-button-style w-100 py-3"
                  onClick={checkNetworkSpeed}
                >
                  <img
                    src={wifiLogo}
                    alt=""
                    className="wifi-responsive-width-style"
                  />
                  <span className="wifi-connection-text ml-3">Check Now</span>
                </button>
              </div>
            </div>
            {showInternetSpeed()}
            <div
              className={`d-flex align-items-center justify-content-center ${
                isMobileView || isTabView
                  ? "internet-connectivity-block-info"
                  : "internet-connectivity-block-info-tabmonitor"
              }`}
            >
              <div className="d-flex align-items-center justify-content-center">
                <img
                  src={errorLogo}
                  alt=""
                  className="logo-responsive-styling"
                />
                <div className="ml-2 display-content-style">
                  A good internet connectivity is essential for providing
                  services to the customers
                </div>
              </div>
            </div>
          </div>
        )}
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>
      <RegistrationFooterButton
        disableNextButton={isSubmitAllowed}
        onPositiveActionClick={onPositiveActionClick}
        onBackButtonClick={onBackButtonClick}
        isLastStep={isLastStep}
      />
    </span>
  );
};
